/* eslint-disable react/no-unescaped-entities */
import react, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { Meta } from '@/layout/Meta'
import { Main } from '@/templates/Main'
import { useCookie } from '@/utils/Session'

import { IConnectedApp } from './api/connected-apps'
import GoogleConnectedApp from './components/connected-apps/google'
import FacebookConnectedApp from './components/connected-apps/facebook'
import BigQueryConnectedApp from './components/connected-apps/bigquery'
import BingConnectedApp from './components/connected-apps/bing'

import { GoogleConfig } from './api/google/data'
import { FacebookConfig } from './api/facebook/data'
import { BigQueryConfig } from './api/bigquery/data'
import { GoogleSheetsConfig } from './api/google-sheets/data'
import { BingConfig } from './api/bing/data'
import GoogleSheetsConnectedApp from './components/connected-apps/googleSheets'
import ReportOptions from './components/connected-apps/report-options'
import { ReportField } from '@/reports/reports'
import MongoConnectedApp from './components/connected-apps/mongo'
import { MongoCredentials } from '@/mongo/mongo'

interface ConnectedAppsProps {
  oauthCallbackBaseUrl: string
  bingClientId: string
}

const ConnectedApps = (props: ConnectedAppsProps) => {
  const [sessionId] = useCookie('sessionId')
  const [connectedApps, setConnectedApps] = react.useState<IConnectedApp[]>([])

  // Report config
  const [reportConfig, setReportConfig] = react.useState<ReportField[]>([] as ReportField[])

  // Google data
  const [googleConnected, setGoogleConnected] = react.useState(false)
  const [googleParams, setGoogleParams] = react.useState<GoogleConfig>()

  // Facebook
  const [facebookConnected, setFacebookConnected] = react.useState(false)
  const [facebookParams, setFacebookParams] = react.useState<FacebookConfig>()

  // Mongo
  const [connectionString, setConnectionString] = react.useState('')
  const [writtenAtField, setWrittenAtField] = react.useState('')
  const [collections, setCollections] = react.useState<MongoCredentials['collections']>({})

  // Big Query
  const [bigQueryConnected, setBigQueryConnected] = react.useState(false)
  const [bigQueryParams, setBigQueryParams] = react.useState<BigQueryConfig>()

  // Google Sheets
  const [googleSheetsConnected, setGoogleSheetsConnected] = react.useState(false)
  const [googleSheetsParams, setGoogleSheetsParams] = react.useState<GoogleSheetsConfig>()

  // Bing
  const [bingConnected, setBingConnected] = react.useState(false)
  const [bingParams, setBingParams] = react.useState<BingConfig>()

  const router = useRouter()
  
  // Navigation
  const [moveNavToTop, setMoveNavToTop] = useState(false)
  const topRef = useRef(null)
  const reportRef = useRef(null)
  const sourcesRef = useRef(null)
  const googleRef = useRef(null)
  const facebookRef = useRef(null)
  const bingRef = useRef(null)
  const destinationsRef = useRef(null)
  const bigQueryRef = useRef(null)
  const googleSheetsRef = useRef(null)

  const handleNav = (ref: any) => {
    ref.current.scrollIntoView({ behavior: 'smooth' })
  }

  /**
   * Check if we're scrolled past the top content item, so we 
   * can move the nav up if so
   */
  useEffect(() => {
    const handleScroll = () => {
      if (reportRef.current) {
        const targetRect = (reportRef.current as any).getBoundingClientRect()
        setMoveNavToTop(targetRect.top < 5);
      }
    };

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  /**
   * Gets all connected apps for user
   * @returns 
   */
  async function fetchConnectedApps() {
    const resp = await fetch('/api/connected-apps', {
      method: 'get',
      headers: {
        Session: sessionId(),
      }
    })

    if (!resp.ok) {
      return false
    }

    return resp.json()
  }

  /**
   * On load, fetch connected apps
   */
  useEffect(() => {
    console.debug('looking for connected apps')

    if (!sessionId()) {
      router.push('/login')
      return
    }
    
    fetchConnectedApps().then((r) => {
      console.info('connected apps->', r)

      if (!r) {
        return
      }

      setConnectedApps(r)

      // google
      setGoogleConnected(r.find((a:any) => a.name === 'google')?.connected)
      setGoogleParams(r.find((a:any) => a.name === 'google')?.params)

      // facebook
      setFacebookConnected(r.find((a:any) => a.name === 'facebook')?.connected)
      setFacebookParams(r.find((a:any) => a.name === 'facebook')?.params)

      // mongo
      setConnectionString(r.find((a:any) => a.name === 'mongo')?.params?.connectionString)
      setWrittenAtField(r.find((a:any) => a.name === 'mongo')?.params?.writtenAtField)
      setCollections(r.find((a:any) => a.name === 'mongo')?.params?.collections)

      // bigquery
      setBigQueryConnected(r.find((a:any) => a.name === 'bigQuery')?.connected)
      setBigQueryParams(r.find((a:any) => a.name === 'bigQuery')?.params)

      // google sheets
      setGoogleSheetsConnected(r.find((a:any) => a.name === 'googleSheets')?.connected)
      setGoogleSheetsParams(r.find((a:any) => a.name === 'googleSheets')?.params)

      // bing
      setBingConnected(r.find((a:any) => a.name === 'bing')?.connected)
      setBingParams(r.find((a:any) => a.name === 'bing')?.params)

      // report config
      setReportConfig(r.find((a:any) => a.name === 'reports')?.params?.default)
    })
  }, [])

  const navStyle = 'text-blue-600 text-left text-sm block'

  return (
    <Main
      meta={
        <Meta
          title="Mappings"
          description="Export your digital advertising data into a SQL database."
        />
      }
    >
      <div ref={topRef}></div>
      <div className='flex flex-col md:flex-col'>
        <nav className={`w-full md:w-32 h-10 -mt-4 px-8 md:px-0 hidden justify-between md:block fixed ${moveNavToTop && 'top-3'}`}>
          <p className="p-0 m-0 hidden md:block text-md">Navigation</p>
          {/* <button className={navStyle} onClick={() => handleNav(topRef)}>Top</button> */}
          <button className={`${navStyle} font-bold`} onClick={() => handleNav(reportRef)}>Reports</button>
          <button className={`${navStyle} font-bold`} onClick={() => handleNav(sourcesRef)}>Sources</button>
          <button className={`${navStyle} hidden md:block text-xs ml-3`} onClick={() => handleNav(googleRef)}>Google</button>
          <button className={`${navStyle} hidden md:block text-xs ml-3`} onClick={() => handleNav(facebookRef)}>Facebook</button>
          <button className={`${navStyle} hidden md:block text-xs ml-3`} onClick={() => handleNav(bingRef)}>Bing</button>
          <button className={`${navStyle} font-bold`} onClick={() => handleNav(destinationsRef)}>Destinations</button>
          <button className={`${navStyle} hidden md:block text-xs ml-3`} onClick={() => handleNav(bigQueryRef)}>BigQuery</button>
          <button className={`${navStyle} hidden md:block text-xs ml-3`} onClick={() => handleNav(googleSheetsRef)}>Google Sheets</button>
        </nav>
        <main className='ml-2 md:ml-32 w-full md:w-5/6 pr-3 mt-10'>
          <h1 className='text-3xl font-bold hidden'>Connected Apps</h1>
          <div className='mb-12 hidden'>
            Connect your data sources and destinations to Dessert Data.
          </div>

          <h2 className='text-3xl text-blue-800 font-bold border-b-2 mb-10' ref={reportRef}>
          ⚙️ Report Format
          </h2>
          <header>
            Select the fields you want to include in your reports.
            <p className='text-sm mt-0 mb-2'>
              These metrics will be synced daily from each connected data source into each connected
              data destination.
            </p>
          </header>
          <ReportOptions 
            checked={reportConfig} 
            sessionId={sessionId()}
          />

          <h2 className='text-3xl text-blue-800 font-bold border-b-2 mb-10' ref={sourcesRef}>
          🔌 Data Sources <button className=' text-sm float-right mt-3 mr-4 underline text-blue-500 ' onClick={() => handleNav(topRef)}>top</button>
          </h2>
            <span ref={googleRef}></span>
            <GoogleConnectedApp
              key={googleParams?.id}
              id={googleParams?.id}
              connections={googleParams?.connections}
              googleManagerId={googleParams?.googleManagerId}
              isConnected={googleConnected}
              sessionId={sessionId()}
              oauthCallbackBaseUrl={props.oauthCallbackBaseUrl}
            />

            <span ref={facebookRef}></span>
            <FacebookConnectedApp
              facebookAdAccountId={facebookParams?.facebookAdAccountId}
              isConnected={facebookConnected}
              sessionId={sessionId()}
              oauthCallbackBaseUrl={props.oauthCallbackBaseUrl}
            />

            <span ref={bingRef}></span>
            <BingConnectedApp
              customerId={bingParams?.customerId}
              customerAccountId={bingParams?.customerAccountId}
              isConnected={bingConnected}
              sessionId={sessionId()}
              oauthCallbackBaseUrl={props.oauthCallbackBaseUrl}
              clientId={props.bingClientId}
            />

            <MongoConnectedApp
              connectionString={connectionString}
              writtenAtField={writtenAtField}
              collections={collections}
              sessionId={sessionId()}
            />


          <h2 className='text-3xl text-blue-800 font-bold border-b-2 mt-28 mb-10' ref={destinationsRef}>
          🌐 Data Destinations <button className=' text-sm float-right mt-3 mr-4 underline text-blue-500 ' onClick={() => handleNav(reportRef)}>top</button>
          </h2>

          <span ref={bigQueryRef}></span>
          <BigQueryConnectedApp
            googleCloudProjectId={bigQueryParams?.googleCloudProjectId}
            bigQueryDatasetId={bigQueryParams?.bigQueryDatasetId}
            isConnected={bigQueryConnected}
            sessionId={sessionId()}
            oauthCallbackBaseUrl={props.oauthCallbackBaseUrl}
          />

          <span ref={googleSheetsRef}></span>
          <GoogleSheetsConnectedApp
            monthlyPerformanceReportSpreadsheetId={googleSheetsParams?.monthlyPerformanceReportSpreadsheetId}
            monthlyPerformanceReportUrl={googleSheetsParams?.monthlyPerformanceReportUrl}
            isConnected={googleSheetsConnected}
            sessionId={sessionId()}
            oauthCallbackBaseUrl={props.oauthCallbackBaseUrl}
          />
        </main>
      </div>
    </Main>
  )
}

export async function getStaticProps() {
  return {
    props: {
      oauthCallbackBaseUrl: process.env.OAUTH_BASE_URL_CALLBACK,
      bingClientId: process.env.BING_CLIENT_ID,
    }
  }
}

export default ConnectedApps